import { TbCheck, TbLoader2 } from "solid-icons/tb";
import type { JSX } from "solid-js";
import { createMemo } from "solid-js";
import { assetLifecycleStatusIcon } from "~/components/utils/lifecycleStateMappers";

type AssetLifecycleStatusGlyphProps = {
  state: string | undefined;
  /** When true and `state` does not map to an icon, render a spinner (e.g. asset not loaded yet). */
  assumeSpinnerWhenUnmapped?: boolean;
  /** Tailwind size classes for the icon (default w-3.5 h-3.5). */
  sizeClass?: string;
  /** Extra classes for the spinner only. */
  spinnerClass?: string;
};

/**
 * Renders exactly one of: green check, amber check, spinner, or nothing.
 * (Avoid multiple sibling `<Show when={() => …}>` — those callbacks can all be treated as truthy.)
 */
export function AssetLifecycleStatusGlyph(props: AssetLifecycleStatusGlyphProps) {
  const node = createMemo((): JSX.Element => {
    const k = assetLifecycleStatusIcon(props.state);
    const size = props.sizeClass ?? "w-3.5 h-3.5";
    const spin = props.spinnerClass ?? "text-muted-text";

    if (k === "check-success") {
      return <TbCheck class={`${size} shrink-0 text-green-600 dark:text-green-400`} aria-hidden="true" />;
    }
    if (k === "check-amber") {
      return <TbCheck class={`${size} shrink-0 text-amber-600 dark:text-amber-400`} aria-hidden="true" />;
    }
    if (k === "spinner") {
      return <TbLoader2 class={`${size} shrink-0 animate-spin ${spin}`} aria-hidden="true" />;
    }
    if (props.assumeSpinnerWhenUnmapped) {
      return <TbLoader2 class={`${size} shrink-0 animate-spin ${spin}`} aria-hidden="true" />;
    }
    return null;
  });

  return <span class="inline-flex shrink-0 items-center justify-center">{node()}</span>;
}
